import axios, { AxiosInstance, AxiosRequestConfig } from "axios";

import { env } from "env";

import { paramsSerializer, createUnauthorizedResponseInterceptor } from "config/axios-utils";
import { getAccessTokenOrRedirect } from "config/msal-token";

/**
 * This mutator file is split into two versions to handle different execution contexts:
 *
 * 1. Build-time (Node.js): When Orval generates API client code, it imports this file
 *    in a Node.js environment. At this stage, we return a simple fallback axios instance
 *    without MSAL interceptors, since browser APIs (window, localStorage, etc.) aren't
 *    available and MSAL requires browser-specific functionality.
 *
 * 2. Runtime (Browser): When the app runs in the browser, we create a fully configured
 *    axios instance with MSAL authentication interceptors and error handling.
 *
 * The `typeof window === "undefined"` check distinguishes between these two contexts.
 */

// Build-time env helper: uses process.env (for Orval code generation)
const getBuildTimeEnv = (key: string): string | undefined => {
  if (typeof process !== "undefined" && process.env) {
    return process.env[key];
  }
  return undefined;
};

// Runtime env helper: uses env.js (includes window.env from deployment)
const getRuntimeEnv = (key: string): string | undefined => {
  if (env && typeof env[key] !== "undefined") {
    return env[key];
  }
  return undefined;
};

// Build-safe helper to get base URL
// Runtime: uses env.js which gets REACT_APP_* from window.env
const getBaseURL = (): string => {
  const baseUrl = getRuntimeEnv("REACT_APP_HARBOR_API_BASE_URL");
  if (!baseUrl) {
    throw new Error("REACT_APP_HARBOR_API_BASE_URL is not defined in environment variables");
  }
  return baseUrl;
};


// Lazy-load the configured axios instance at runtime
let configuredInstance: AxiosInstance | null = null;
let fallbackInstance: AxiosInstance | null = null;

// Fallback axios instance for Orval code generation
// Build time: uses process.env which has VITE_* from .env file
const getFallbackInstance = (): AxiosInstance => {
  if (fallbackInstance) {
    return fallbackInstance;
  }

  const baseURL = getBuildTimeEnv("VITE_HARBOR_API_BASE_URL");
  if (!baseURL) {
    throw new Error(
      "VITE_HARBOR_API_BASE_URL is not defined in environment variables. Please set it in your .env file.",
    );
  }

  fallbackInstance = axios.create({
    baseURL,
    headers: {
      "Content-Type": "application/json",
    },
    paramsSerializer,
  });

  return fallbackInstance;
};

const createConfiguredInstance = (): AxiosInstance => {
  // Return fallback during build time (when window is undefined)
  if (typeof window === "undefined") {
    return getFallbackInstance();
  }

  // Create axios instance with configured base URL
  const instance = axios.create({
    baseURL: getBaseURL(),
    paramsSerializer,
  });

  // Request interceptor for authentication
  instance.interceptors.request.use(async (config) => {
    // Public endpoints: no auth
    const url = config.url ?? "";
    if (url === "/management/info") {
      return config;
    }

    const token = await getAccessTokenOrRedirect();
    config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
    return config;
  });
  instance.interceptors.response.use(
    (response) => response,
    createUnauthorizedResponseInterceptor(),
  );

  return instance;
};

const getInstance = (): AxiosInstance => {
  if (configuredInstance) {
    return configuredInstance;
  }

  configuredInstance = createConfiguredInstance();
  return configuredInstance;
};

export const customInstance = <T = unknown>(config: AxiosRequestConfig): Promise<T> => {
  const instance = getInstance();
  return instance.request<T>(config).then(({ data }) => data);
};

export default customInstance;
